import { Link } from "react-router-dom";
import type { ReactNode } from "react";

/**
 * Public pages: legal, marketing and support. Rendered inside the SPA rather
 * than as separate static files so the header, styling and routing stay
 * consistent, and so links from the onboarding consent step resolve without
 * leaving the app.
 */
export function PublicLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-gray-200 bg-white dark:border-[#2e2e2e] dark:bg-[#1a1a1a]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="font-semibold text-gray-900 dark:text-gray-100">
            Load &amp; Flow
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-emerald-700 dark:text-emerald-400"
            data-testid="public-link-login"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
        <div className="mt-6 space-y-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {children}
        </div>
      </main>
    </div>
  );
}

function H2({ children }: { children: ReactNode }) {
  return (
    <h2 className="pt-4 text-base font-semibold text-gray-900 dark:text-gray-100">{children}</h2>
  );
}

export function TermsPage() {
  return (
    <PublicLayout title="Terms of use">
      <p data-testid="terms-page">
        By using Load &amp; Flow you agree to these terms. If you don't agree, please don't use
        the service.
      </p>

      <H2>Not medical advice</H2>
      <p>
        Load &amp; Flow prescribes kettlebell and bodyweight training based on the information you
        give it. It is not a medical device, it does not know your health history, and nothing it
        produces is medical advice. Training carries a risk of injury. You are responsible for
        judging whether an exercise is appropriate for you, and for stopping if something hurts.
      </p>

      <H2>Your account</H2>
      <p>
        You must be 18 or over. Sign-in links are single-use and personal to you — anyone with
        access to your email can sign in as you, so keep it secure.
      </p>

      <H2>Availability</H2>
      <p>
        The service is provided as-is, without a guarantee of uptime. We may change or withdraw
        features.
      </p>

      <H2>Ending your use</H2>
      <p>
        You can delete your account at any time from Settings. Deletion is reversible for a short
        grace period, after which your data is permanently removed.
      </p>
    </PublicLayout>
  );
}

export function PrivacyPage() {
  return (
    <PublicLayout title="Privacy policy">
      <p data-testid="privacy-page">
        This explains what Load &amp; Flow stores about you, why, and what you can do about it.
      </p>

      <H2>What we store</H2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Your email address, used to sign you in.</li>
        <li>Your training preferences and the kettlebells you own.</li>
        <li>Your sessions, the sets you log, and how hard they felt.</li>
        <li>Activity data from Strava, only if you connect it yourself.</li>
      </ul>

      <H2>Why we store it</H2>
      <p>
        To provide the service you asked for — planning sessions, tracking fatigue and progressing
        weight over time. Your training data is not sold, and is not used to advertise to you.
      </p>

      <H2>Where it is stored</H2>
      <p>
        Your data is held in the European Union. See{" "}
        <Link to="/sub-processors" className="text-emerald-700 underline dark:text-emerald-400">
          sub-processors
        </Link>{" "}
        for the specific providers and locations.
      </p>

      <H2>Your rights</H2>
      <p>
        Under the GDPR you can access, export and erase your data. Both are self-service from
        Settings: <em>Download my data</em> returns everything we hold about you as JSON, and{" "}
        <em>Delete my account</em> schedules permanent erasure.
      </p>
    </PublicLayout>
  );
}

interface SubProcessor {
  provider: string;
  purpose: string;
  data: string;
  location: string;
  basis: string;
}

/**
 * Kept accurate against the deployed infrastructure — this is a GDPR
 * disclosure, so a stale entry is a compliance problem rather than a cosmetic
 * one. Updated for the edge migration: hosting and database moved from
 * self-hosted OVH VPS to Bunny.net, and transactional email from Brevo to
 * Forward Email.
 */
const SUB_PROCESSORS: SubProcessor[] = [
  {
    provider: "Bunny.net (Edge Scripting)",
    purpose: "Application hosting and CDN",
    data: "IP addresses, request headers (transient)",
    location: "EU edge nodes",
    basis: "DPA, EU-based",
  },
  {
    provider: "Bunny.net (Edge SQL)",
    purpose: "Application database",
    data: "All user data",
    location: "Paris, France",
    basis: "DPA, EU-based",
  },
  {
    provider: "Bunny.net (Storage)",
    purpose: "Data exports and backups",
    data: "Encrypted exports and media",
    location: "Europe (eu-west-1)",
    basis: "DPA, EU-based",
  },
  {
    provider: "Bunny.net Stream",
    purpose: "Exercise video hosting",
    data: "No personal data",
    location: "EU edge nodes",
    basis: "DPA, EU-based",
  },
  {
    provider: "Forward Email",
    purpose: "Transactional email delivery",
    data: "Email addresses, message content",
    location: "EU",
    basis: "DPA",
  },
  {
    provider: "Strava",
    purpose: "Activity sync, only if you connect it",
    data: "Activity data you authorise",
    location: "US",
    basis: "Your consent (Art. 6(1)(a))",
  },
];

export function SubProcessorsPage() {
  return (
    <PublicLayout title="Sub-processors">
      <p data-testid="subprocessors-page">
        Load &amp; Flow uses the following third parties to deliver the service. Everything except
        Strava — which only receives data if you explicitly connect it — is EU-based.
      </p>

      <div className="overflow-x-auto">
        <table className="mt-4 w-full text-left text-xs">
          <thead className="border-b border-gray-200 dark:border-[#2e2e2e]">
            <tr>
              {["Provider", "Purpose", "Data", "Location", "Legal basis"].map((h) => (
                <th key={h} className="py-2 pr-3 font-semibold text-gray-900 dark:text-gray-100">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#2e2e2e]">
            {SUB_PROCESSORS.map((p) => (
              <tr key={p.provider} data-testid={`subprocessor-${p.provider}`}>
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                  {p.provider}
                </td>
                <td className="py-2 pr-3">{p.purpose}</td>
                <td className="py-2 pr-3">{p.data}</td>
                <td className="py-2 pr-3">{p.location}</td>
                <td className="py-2 pr-3">{p.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PublicLayout>
  );
}

export function CookiesPage() {
  return (
    <PublicLayout title="Cookies">
      <p data-testid="cookies-page">
        Load &amp; Flow uses no advertising or analytics cookies, so there is no cookie banner to
        dismiss.
      </p>
      <p>
        Your sign-in session is held in your browser's local storage so you stay signed in between
        visits. Clearing your browser data signs you out.
      </p>
    </PublicLayout>
  );
}

export function SupportPage() {
  return (
    <PublicLayout title="Support">
      <p data-testid="support-page">Common questions, and how to get in touch.</p>

      <H2>I didn't get my sign-in link</H2>
      <p>
        Check your spam folder. Links expire and can only be used once, so request a fresh one if
        an older link stops working.
      </p>

      <H2>The planner isn't suggesting anything</H2>
      <p>
        The planner only prescribes weights you own, so add your kettlebells in Settings first. It
        also respects your experience level, which caps exercise complexity.
      </p>

      <H2>Getting your data out</H2>
      <p>
        Settings → <em>Download my data</em> gives you everything we hold as a JSON file, limited
        to one export per day.
      </p>

      <H2>Contact</H2>
      <p>
        Email{" "}
        <a
          href="mailto:ollie@loadandflow.com"
          className="text-emerald-700 underline dark:text-emerald-400"
          data-testid="support-link-email"
        >
          ollie@loadandflow.com
        </a>
        .
      </p>
    </PublicLayout>
  );
}
